import { Star, Quote } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Testimonial {
  name: string;
  role: string;
  quote: string;
}

interface TestimonialsProps {
  sectionId?: string | null;
}

export function Testimonials({ sectionId = "testimonials" }: TestimonialsProps) {
  const { t } = useTranslation();
  const testimonials: Testimonial[] = [
    {
      name: "Helina Tuuna",
      role: "Stell Eesti AS",
      quote: "Täname kogu 4Sisters Events'i tiimi suurepärase uusaasta peo korralduse eest! Kogu sündmus oli väga hästi organiseeritud, toit maitsev ja kvaliteetne. Eriti soovin rõhutada suurepärast koostööd kogu ettevalmistusprotsessi vältel. Suhtlus oli avatud ja operatiivne, kokkulepetest peeti kinni ning lahendused leiti kiiresti ka jooksvalt tekkinud küsimustele. Selline partnerlus annab kindlustunde ka tulevaste ürituste planeerimisel. Oli rõõm koos töötada. Edu!"
    },
    {
      name: "Eve Kaljusaar",
      role: "Turundus- ja kommunikatsioonijuht, AS Arstikeskus Confido",
      quote: "Kristin ja 4Sisters Events on olnud meie jaoks rohkem kui sündmusturunduse teenusepakkuja – nad on olnud tõelised partnerid. Nendega koostööd tehes on alati tunne, et projekt on heades kätes. Aastate jooksul on nad tõestanud, et suudavad mõista meie vajadusi, pakkuda loovaid lahendusi ning viia ideed ellu professionaalselt ja detailitäpselt. Hindame nende võimet tulla toime ka ootamatute olukordadega, mida sündmuste korraldamisel ikka ette tuleb."
    },
    {
      name: "Cipax Eesti AS",
      role: "",
      quote: "Aitäh, 4Sisters Events korraldamast meile meeleolukad suvepäevad, millega jäid kõik väga rahule ja otsustasime edaspidigi koostööd teha, et järgmisedki suvepäevad nii vahvalt välja kukuks!"
    }
  ];

  const renderStars = () => (
    <div className="text-accent mb-4 flex">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-current" />
      ))}
    </div>
  );

  return (
    <section id={sectionId ?? undefined} className="py-20 bg-gray-50" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-poppins font-bold text-4xl md:text-5xl text-dark mb-6" data-testid="testimonials-title">
            {t("testimonials.title")} <span className="text-primary">{t("testimonials.titleHighlight")}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-testid="testimonials-subtitle">
            {t("testimonials.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={testimonial.name}
              className="bg-white rounded-2xl p-8 shadow-lg card-hover relative"
              data-testid={`testimonial-${index + 1}`}
            >
              <Quote className="w-8 h-8 text-accent/30 mb-4" />
              
              {renderStars()}
              <p className="text-gray-700 italic mb-6 leading-relaxed" data-testid={`testimonial-quote-${index + 1}`}>
                "{testimonial.quote}"
              </p>

              <div className="border-t border-gray-100 pt-4">
                <h3 className="font-semibold text-lg text-dark" data-testid={`testimonial-name-${index + 1}`}>
                  {testimonial.name}
                </h3>
                {testimonial.role && (
                  <p className="text-gray-600 text-sm" data-testid={`testimonial-role-${index + 1}`}>
                    {testimonial.role}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
